import { useCallback, useEffect, useRef, useState } from "react";
import { listarCargos, crearCargo, anularCargo } from "../../../../services/cargosAdicionalesService";
import { showToast } from "../../../../utils/showToast";
import { getSocket, initSocket } from "../../../../utils/socketClient";

const PAGE_SIZE = 15;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function useCargosAdicionales() {
  const [cargos, setCargos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [estadoFiltro, setEstadoFiltro] = useState("pendiente");

  const fetchCargos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarCargos({
        search: search || undefined,
        estado: estadoFiltro !== "all" ? estadoFiltro : undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setCargos(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(data?.total ?? 0);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudieron cargar los cargos adicionales.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, [search, estadoFiltro, page]);

  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      // Escalonada: junto con usePagosCobros y el resumen de pagos, esta
      // pantalla ya dispara varias peticiones simultaneas al montar.
      let cancelled = false;
      wait(400).then(() => {
        if (!cancelled) fetchCargos();
      });
      return () => {
        cancelled = true;
      };
    }
    fetchCargos();
    return undefined;
  }, [fetchCargos]);

  const refreshTimeoutRef = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) return;
    refreshTimeoutRef.current = setTimeout(() => {
      refreshTimeoutRef.current = null;
      fetchCargos();
    }, 400);
  }, [fetchCargos]);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    const socket = getSocket() || initSocket(token);
    if (!socket) return undefined;

    const onCargosChanged = () => scheduleRefresh();
    socket.on("cargos:actualizados", onCargosChanged);
    return () => socket.off("cargos:actualizados", onCargosChanged);
  }, [scheduleRefresh]);

  const crear = useCallback(async (payload) => {
    const data = await crearCargo(payload);
    await fetchCargos();
    return data;
  }, [fetchCargos]);

  const anular = useCallback(async (id) => {
    try {
      await anularCargo(id);
      showToast({ type: "success", message: "Cargo anulado correctamente." });
    } catch (err) {
      showToast({ type: "danger", message: err?.message || "No se pudo anular el cargo." });
    } finally {
      await fetchCargos();
    }
  }, [fetchCargos]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const goToPage = useCallback(
    (next) => setPage(Math.min(totalPages, Math.max(1, next))),
    [totalPages]
  );

  useEffect(() => { setPage(1); }, [search, estadoFiltro]);

  return {
    cargos,
    total,
    loading,
    error,
    lastUpdated,
    search,
    setSearch,
    estadoFiltro,
    setEstadoFiltro,
    page,
    pageSize: PAGE_SIZE,
    totalPages,
    goToPage,
    reload: fetchCargos,
    crear,
    anular,
  };
}
